import { createFileRoute } from "@tanstack/react-router";
import { Check } from "@/components/animate-ui/icons/check";
import { PublicLayout } from "@/components/public-layout";
import { buildLinks, buildMetaTags, getCanonical } from "@/lib/seo";

export const Route = createFileRoute("/privacy-policy")({
	head: () => {
		const canonical = getCanonical("/privacy-policy");
		const meta = buildMetaTags({
			title: "Privacy Policy - m8tes",
			description:
				"Our privacy policy explains how m8tes collects, uses, and protects your personal information.",
			canonical,
			ogType: "website",
		});
		return {
			meta,
			links: buildLinks({ canonical }),
		};
	},
	component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
	return (
		<PublicLayout>
			<div className="container mx-auto px-4 py-12 max-w-4xl">
				<div className="prose prose-neutral dark:prose-invert max-w-none">
					<h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
					<p className="text-muted-foreground mb-8">Effective Date: November 7, 2025</p>

					{/* TLDR */}
					<section className="mb-12 p-6 bg-primary/10 border-l-4 border-primary rounded-r-lg">
						<h2 className="text-2xl font-semibold mb-4">TL;DR - What You Need to Know</h2>
						<ul className="space-y-3">
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>No AI Training on Your Data</strong> - We never use your content,
									conversations, or outputs to train AI models
								</div>
							</li>
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>You Own Your Data</strong> - Full ownership of all content and outputs you
									create
								</div>
							</li>
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>Delete Anytime</strong> - Request deletion of all your data at any time
								</div>
							</li>
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>GDPR Compliant</strong> - Full privacy rights for EEA/UK users
								</div>
							</li>
							<li className="flex items-start gap-3">
								<Check animateOnHover className="text-primary size-5 mt-0.5 shrink-0" />
								<div>
									<strong>No Data Selling</strong> - We never sell your information to third parties
								</div>
							</li>
						</ul>
					</section>

					{/* Introduction */}
					<section className="mb-8">
						<p>
							m8tes, Inc. ("<strong>m8tes</strong>," "<strong>we</strong>," "<strong>us</strong>,"
							or "<strong>our</strong>") is committed to protecting your privacy. This Privacy
							Policy explains how we collect, use, disclose, and safeguard your information when you
							use our autonomous AI marketing assistant platform (the "<strong>Service</strong>").
						</p>
						<p>
							Please read this Privacy Policy carefully. By accessing or using our Service, you
							acknowledge that you have read, understood, and agree to be bound by this Privacy
							Policy. If you do not agree with our policies and practices, please do not use our
							Service.
						</p>
					</section>

					{/* 1. Information We Collect */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">1. Information We Collect</h2>

						<h3 className="text-2xl font-semibold mt-8 mb-3">1.1 Information You Provide</h3>
						<p>We collect information that you provide directly to us:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Account Information:</strong> Email address, name, phone number (optional),
								and password when you create an account
							</li>
							<li>
								<strong>Agent Configuration:</strong> Agent names, roles, instructions, goals, and
								preferences you set for your autonomous teammates
							</li>
							<li>
								<strong>Task and Conversation Data:</strong> Instructions, queries, messages, and
								content you submit to our Service, including the AI-generated responses
							</li>
							<li>
								<strong>Integration Credentials:</strong> When you connect third-party services
								(such as advertising platforms), we collect and securely store authentication
								credentials
							</li>
							<li>
								<strong>Billing Information:</strong> Payment information is processed by our
								third-party payment processor and we store only transaction references and
								subscription status
							</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">
							1.2 Information We Collect Automatically
						</h3>
						<p>When you use our Service, we automatically collect certain information:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Usage Information:</strong> Request timestamps, response times, token
								counts, execution metrics, and task completion status
							</li>
							<li>
								<strong>Log Data:</strong> IP addresses, request methods, URL paths, HTTP status
								codes, and error messages for security and diagnostic purposes
							</li>
							<li>
								<strong>Cost Tracking:</strong> API usage costs and execution time for billing and
								analytics
							</li>
						</ul>
					</section>

					{/* 2. Google User Data (When You Connect Google Integrations) */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">
							2. Google User Data (When You Connect Google Integrations)
						</h2>
						<p className="mb-4">
							<strong>This section only applies if you choose to connect Google services</strong>{" "}
							(such as Google Ads or Google OAuth) to your m8tes account. If you do not connect
							these integrations, we do not collect any Google user data.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">
							2.1 What Google User Data We Collect
						</h3>
						<p>When you actively connect Google integrations to m8tes, we collect:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Google Account Information:</strong> Your email address, name, and profile
								information provided during OAuth authentication
							</li>
							<li>
								<strong>Google Ads Customer IDs:</strong> The customer account identifiers you
								authorize us to access
							</li>
							<li>
								<strong>Authentication Credentials:</strong> OAuth tokens (access tokens and refresh
								tokens) required to authenticate your requests to Google's services
							</li>
						</ul>
						<p className="mt-4">
							<strong>Important:</strong> We do <em>not</em> permanently store your Google Ads
							campaign data, metrics, or advertising information. We provide you with a query tool
							(GAQL - Google Ads Query Language) that allows you to retrieve data directly from
							Google on demand. Query results are temporarily processed to display to you but are
							not saved to our databases unless you explicitly save specific information within the
							Service.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">2.2 How We Use Google User Data</h3>
						<p>We use Google user data solely to provide you with the services you requested:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Authentication:</strong> To verify your identity and maintain your login
								session via Google OAuth
							</li>
							<li>
								<strong>Execute Queries:</strong> To run Google Ads Query Language (GAQL) queries on
								your behalf and retrieve advertising data you request
							</li>
							<li>
								<strong>Display Results:</strong> To process and display query results, campaign
								performance data, and metrics within our interface
							</li>
							<li>
								<strong>Maintain Access:</strong> To refresh authentication tokens and maintain
								authorized access to your connected Google Ads accounts
							</li>
						</ul>
						<p className="mt-4">
							<strong>We do NOT use Google user data for:</strong>
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Training artificial intelligence or machine learning models</li>
							<li>Targeted advertising, personalized advertising, or retargeted advertising</li>
							<li>Selling to data brokers or information resellers</li>
							<li>Determining credit-worthiness or lending purposes</li>
							<li>Any purpose unrelated to providing or improving m8tes functionality</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">
							2.3 How We Share, Transfer, or Disclose Google User Data
						</h3>
						<p>
							We do <strong>not</strong> sell, rent, or transfer your Google user data to third
							parties. We only share Google user data in the following limited circumstances:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>With Google Services:</strong> We transmit authentication credentials and
								query requests to Google's APIs solely to execute the services you requested (e.g.,
								running GAQL queries against your Google Ads accounts)
							</li>
							<li>
								<strong>With Your Consent:</strong> If you explicitly authorize us to share data
								with additional third-party services you connect
							</li>
							<li>
								<strong>Legal Compliance:</strong> If required by law, court order, or government
								regulation (as described in Section 4.3)
							</li>
						</ul>
						<p className="mt-4">
							We do <strong>not</strong> transfer or disclose Google user data to third parties for
							purposes other than providing you with the m8tes Service functionality you requested.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">2.4 Security of Google User Data</h3>
						<p>We protect your Google user data with industry-standard security measures:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Encryption:</strong> All Google OAuth tokens (access tokens, refresh tokens)
								are encrypted at rest using AES-256 encryption and transmitted over secure HTTPS
								connections
							</li>
							<li>
								<strong>Access Controls:</strong> Access to Google user data is restricted to
								authorized systems and personnel only
							</li>
							<li>
								<strong>Secure Storage:</strong> Authentication credentials are stored in encrypted
								database fields with strict access policies
							</li>
							<li>
								<strong>Token Management:</strong> We use short-lived access tokens and securely
								manage refresh token rotation
							</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">
							2.5 Retention and Deletion of Google User Data
						</h3>
						<p>We retain Google user data only as long as necessary:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Authentication Credentials:</strong> Stored for as long as you maintain the
								Google integration connection. You may disconnect at any time through your account
								settings.
							</li>
							<li>
								<strong>Query Results:</strong> Temporarily processed for display but not
								permanently stored unless you explicitly save specific data
							</li>
							<li>
								<strong>Account Deletion:</strong> When you delete your m8tes account or disconnect
								the Google integration, we immediately delete all associated Google authentication
								credentials and any stored Google user data
							</li>
						</ul>
						<p className="mt-4">
							<strong>You may request deletion of your Google user data at any time</strong> by
							emailing support@m8tes.ai or by disconnecting the Google integration in your account
							settings.
						</p>
					</section>

					{/* 3. How We Use Your Information */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">3. How We Use Your Information</h2>
						<p>We use the information we collect to:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Provide and Improve the Service:</strong> Execute your tasks, run autonomous
								agents, and continuously improve our platform
							</li>
							<li>
								<strong>Process Transactions:</strong> Manage your subscription, process payments,
								and send billing-related communications
							</li>
							<li>
								<strong>Maintain Integrations:</strong> Connect to and authenticate with third-party
								services you authorize
							</li>
							<li>
								<strong>Security and Fraud Prevention:</strong> Detect, prevent, and address
								security incidents and fraudulent activity
							</li>
							<li>
								<strong>Customer Support:</strong> Respond to your questions, troubleshoot issues,
								and provide technical assistance
							</li>
							<li>
								<strong>Analytics and Development:</strong> Analyze usage patterns to improve
								features, develop new capabilities, and optimize performance
							</li>
							<li>
								<strong>Compliance:</strong> Comply with legal obligations and enforce our terms of
								service
							</li>
						</ul>
					</section>

					{/* 4. How We Share Your Information */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">4. How We Share Your Information</h2>
						<p>We may share your information with the following categories of third parties:</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">4.1 Service Providers</h3>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>AI Service Providers:</strong> We use artificial intelligence services to
								power our autonomous agents and process your tasks and conversations
							</li>
							<li>
								<strong>Payment Processors:</strong> To process subscription payments and manage
								billing
							</li>
							<li>
								<strong>Cloud Infrastructure:</strong> For hosting, data storage, and computing
								resources
							</li>
							<li>
								<strong>Email Service Providers:</strong> To send transactional emails such as
								verification and notifications
							</li>
							<li>
								<strong>Error Tracking Services:</strong> To monitor application health and diagnose
								technical issues (optional)
							</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">4.2 Third-Party Integrations</h3>
						<p>
							When you connect third-party services (such as advertising platforms) to your agents,
							we share relevant data with those services as necessary to execute your tasks. These
							integrations are subject to the privacy policies of those third-party services.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">4.3 Legal Requirements</h3>
						<p>
							We may disclose your information if required by law, court order, or government
							regulation, or if we believe such action is necessary to:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Comply with legal obligations</li>
							<li>Protect our rights, property, or safety</li>
							<li>Prevent fraud or security threats</li>
							<li>Protect the rights and safety of our users</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">4.4 Business Transfers</h3>
						<p>
							In the event of a merger, acquisition, reorganization, or sale of assets, your
							information may be transferred as part of that transaction. We will notify you of any
							such change and any choices you may have.
						</p>
					</section>

					{/* 5. Data Security */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">5. Data Security</h2>
						<p>
							We implement appropriate technical and organizational measures to protect your
							information:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Encryption:</strong> We encrypt sensitive data including authentication
								tokens and third-party credentials both in transit and at rest
							</li>
							<li>
								<strong>Secure Authentication:</strong> Passwords are hashed using industry-standard
								algorithms and we support OAuth 2.0 for third-party authentication
							</li>
							<li>
								<strong>Access Controls:</strong> We restrict access to personal information to
								authorized personnel only
							</li>
							<li>
								<strong>Monitoring:</strong> We monitor our systems for security vulnerabilities and
								unauthorized access
							</li>
						</ul>
						<p className="mt-4">
							However, no method of transmission over the internet or electronic storage is 100%
							secure. While we strive to use commercially acceptable means to protect your
							information, we cannot guarantee absolute security.
						</p>
					</section>

					{/* 6. Your Privacy Rights */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">6. Your Privacy Rights</h2>
						<p>
							Depending on your location, you may have the following rights regarding your personal
							information:
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">6.1 General Rights</h3>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Access:</strong> Request a copy of the personal information we hold about
								you
							</li>
							<li>
								<strong>Correction:</strong> Request correction of inaccurate or incomplete
								information
							</li>
							<li>
								<strong>Deletion:</strong> Request deletion of your personal information, subject to
								certain exceptions
							</li>
							<li>
								<strong>Data Portability:</strong> Request a copy of your data in a structured,
								machine-readable format
							</li>
							<li>
								<strong>Opt-Out:</strong> Opt out of certain data processing activities such as
								marketing communications
							</li>
						</ul>

						<h3 className="text-2xl font-semibold mt-8 mb-3">6.2 Rights for EEA/UK Users (GDPR)</h3>
						<p>
							If you are located in the European Economic Area or United Kingdom, you have
							additional rights under GDPR:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>
								<strong>Right to Object:</strong> Object to processing of your personal information
								for direct marketing or legitimate interests
							</li>
							<li>
								<strong>Right to Restrict Processing:</strong> Request that we limit how we use your
								information
							</li>
							<li>
								<strong>Right to Withdraw Consent:</strong> Where we rely on consent, you may
								withdraw it at any time
							</li>
							<li>
								<strong>Right to Lodge a Complaint:</strong> File a complaint with your local data
								protection authority
							</li>
						</ul>
						<p className="mt-4">
							<strong>Legal Basis for Processing (GDPR):</strong> We process your personal
							information based on: (1) your consent; (2) performance of a contract with you; (3)
							compliance with legal obligations; or (4) our legitimate interests in providing and
							improving the Service.
						</p>

						<h3 className="text-2xl font-semibold mt-8 mb-3">6.3 How to Exercise Your Rights</h3>
						<p>
							To exercise any of these rights, please contact us at support@m8tes.ai. We will
							respond to your request within 30 days (or as required by applicable law). We may need
							to verify your identity before processing your request.
						</p>
						<p className="mt-4">
							<strong>Non-Discrimination:</strong> We will not discriminate against you for
							exercising any of your privacy rights.
						</p>
					</section>

					{/* 7. Data Retention */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">7. Data Retention</h2>
						<p>We retain your information for as long as necessary to:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Provide our Service and maintain your account</li>
							<li>Comply with legal obligations (e.g., tax and accounting records)</li>
							<li>Resolve disputes and enforce our agreements</li>
							<li>Improve and develop our Service</li>
						</ul>
						<p className="mt-4">
							When you delete your account, we will delete or anonymize your personal information,
							except where we are required to retain it by law. Conversation logs and task execution
							history associated with your account will be deleted along with your account.
						</p>
					</section>

					{/* 8. International Data Transfers */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">8. International Data Transfers</h2>
						<p>
							Your information may be transferred to and processed in countries other than your
							country of residence. These countries may have different data protection laws than
							your jurisdiction.
						</p>
						<p className="mt-4">
							When we transfer data from the European Economic Area or United Kingdom to other
							countries, we use appropriate safeguards such as Standard Contractual Clauses approved
							by the European Commission to protect your information.
						</p>
					</section>

					{/* 9. Children's Privacy */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">9. Children's Privacy</h2>
						<p>
							Our Service is not intended for individuals under the age of 18. We do not knowingly
							collect personal information from children. If you believe we have collected
							information from a child, please contact us immediately and we will delete such
							information.
						</p>
					</section>

					{/* 10. Changes to This Privacy Policy */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">
							10. Changes to This Privacy Policy
						</h2>
						<p>
							We may update this Privacy Policy from time to time to reflect changes in our
							practices or legal requirements. We will notify you of any material changes by:
						</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Posting the updated policy on this page</li>
							<li>Updating the "Effective Date" at the top</li>
							<li>Sending you an email notification (for significant changes)</li>
						</ul>
						<p className="mt-4">
							Your continued use of the Service after any changes indicates your acceptance of the
							updated Privacy Policy.
						</p>
					</section>

					{/* 11. Contact Us */}
					<section className="mb-8">
						<h2 className="text-3xl font-semibold mt-12 mb-4">11. Contact Us</h2>
						<p>
							If you have questions, concerns, or requests regarding this Privacy Policy or our data
							practices, please contact us:
						</p>
						<div className="bg-muted p-6 rounded-lg mt-4">
							<p>
								<strong>m8tes, Inc.</strong>
							</p>
							<p>Email: support@m8tes.ai</p>
						</div>
						<p className="mt-4">
							<strong>For EEA/UK Users:</strong> If you are located in the European Economic Area or
							United Kingdom, m8tes, Inc. is the data controller of your personal information.
						</p>
					</section>

					{/* Acknowledgment */}
					<section className="mb-8 p-6 bg-muted rounded-lg">
						<p className="text-sm">
							By using our Service, you acknowledge that you have read and understood this Privacy
							Policy and agree to our collection, use, and disclosure of your information as
							described herein.
						</p>
					</section>
				</div>
			</div>
		</PublicLayout>
	);
}
